// Network Summary — Step 5 Results.
// Displays per-site network sizing outcomes.

import type { NetworkResult } from '../../types';

interface Props {
  network: NetworkResult[];
}

export function NetworkSummary({ network }: Props) {
  if (network.length === 0) {
    return <p style={{ color: '#64748b', fontStyle: 'italic' }}>No network results available.</p>;
  }

  const primary = network.find(r => r.site === 'primary');
  const dr = network.find(r => r.site === 'dr');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
      {primary && <NetworkCard result={primary} label="Primary Site" colour="#7c2d12" />}
      {dr      && <NetworkCard result={dr}      label="DR Site"      colour="#9d174d" />}
    </div>
  );
}

function NetworkCard({ result, label, colour }: { result: NetworkResult; label: string; colour: string }) {
  return (
    <div style={cardStyle}>
      {/* Site header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ width: 4, height: 20, borderRadius: 2, background: colour }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{label}</h3>
      </div>

      {/* Bandwidth breakdown */}
      <div style={{ marginBottom: 14 }}>
        <SectionTitle label="Bandwidth" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
          <Metric label="East-West (incl. vMotion)" value={`${result.eastWestBandwidthMbps.toLocaleString()} Mbps`} />
          <Metric label="North-South" value={`${result.northSouthBandwidthMbps.toLocaleString()} Mbps`} />
          <Metric label="Total VM Bandwidth" value={`${result.totalVmBandwidthMbps.toLocaleString()} Mbps`} />
          <Metric
            label="Recommended Uplink"
            value={`${result.recommendedUplinkGbps} Gbps`}
            highlight
            colour={colour}
          />
        </div>
      </div>

      {/* Switching */}
      <div style={{ marginBottom: 14 }}>
        <SectionTitle label="Switching" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <Metric
            label="Access Switch Ports"
            value={result.recommendedAccessPortCount.toLocaleString()}
            highlight
            colour={colour}
          />
          <Metric
            label="Required VLANs"
            value={result.requiredVlanCount.toLocaleString()}
          />
        </div>
      </div>

      {/* Notes */}
      {result.notes.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', userSelect: 'none' }}>
            Methodology notes ({result.notes.length})
          </summary>
          <ol style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
            {result.notes.map((n, i) => (
              <li key={i} style={{ fontSize: 12, color: '#475569', marginBottom: 3, lineHeight: 1.5 }}>{n}</li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

function SectionTitle({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#7c2d12',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      marginBottom: 6,
    }}>
      {label}
    </div>
  );
}

function Metric({ label, value, highlight, colour }: {
  label: string;
  value: string;
  highlight?: boolean;
  colour?: string;
}) {
  const c = colour ?? '#1e40af';
  return (
    <div style={{
      padding: '6px 8px',
      background: highlight ? c + '0d' : '#fafafa',
      borderRadius: 6,
      border: highlight ? `1px solid ${c}33` : '1px solid #f1f5f9',
    }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 15,
        fontWeight: 700,
        color: highlight ? c : '#1e293b',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
